import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight, FaShieldAlt, FaCertificate, FaUserShield, FaSearchPlus, FaBug, FaUserSecret, FaCrosshairs } from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import { getRevealProps, staggerContainer, staggerItem, defaultViewport } from '../motion/scrollMotion';
import './ServiceDetailPage.css';

const certifications = [
  {
    icon: <FaShieldAlt />,
    name: 'CISSP',
    full: 'Certified Information Systems Security Professional',
    body: 'The benchmark credential for security architecture, risk management, and program-level thinking across eight domains. Held by our senior consultants, it is why our reports connect a technical finding to actual business risk instead of just listing CVEs.',
  },
  {
    icon: <FaBug />,
    name: 'CEH',
    full: 'Certified Ethical Hacker',
    body: 'A hands-on credential focused specifically on attacker tools, techniques, and methodology. It is the baseline certification we expect from anyone running a penetration test, alongside OSCP for engagements that call for deeper exploit development.',
  },
  {
    icon: <FaUserSecret />,
    name: 'OSCP',
    full: 'Offensive Security Certified Professional',
    body: 'Widely regarded as the toughest hands-on penetration testing certification to earn — a 24-hour practical exam with no multiple choice, just live systems to compromise. It is the credential behind the manual exploitation work in our VAPT engagements.',
  },
  {
    icon: <FaCrosshairs />,
    name: 'CPENT',
    full: 'Certified Penetration Testing Professional',
    body: "EC-Council's advanced, fully hands-on pentest certification — earned against a live enterprise-grade range covering pivoting, privilege escalation, Active Directory attacks, and exploiting segmented and IoT/OT networks. It backs the depth of our internal, network, and AD-focused engagements beyond a standard external test.",
  },
  {
    icon: <FaSearchPlus />,
    name: 'CISA',
    full: 'Certified Information Systems Auditor',
    body: 'An ISACA credential focused on auditing, controls, and governance of information systems. This is the credential behind our compliance and vCISO engagements — building risk registers, security roadmaps, and audit evidence that actually holds up.',
  },
  {
    icon: <FaCertificate />,
    name: 'ISO 27001 Lead Auditor',
    full: 'Lead Auditor, ISO/IEC 27001:2022',
    body: 'Qualifies our advisors to run gap assessments and internal audits against the same standard your certification body will audit you on. It is the difference between guessing what an auditor will flag and knowing, because we have sat on both sides of that table.',
  },
  {
    icon: <FaUserShield />,
    name: 'CSA',
    full: 'Certified SOC Analyst',
    body: 'A EC-Council credential built specifically for SOC analysts — log analysis, incident triage, and threat detection workflows. It is the baseline certification behind the team staffing our 24/7 Security Operations Centre.',
  },
];

const CertificationsPage = () => (
  <div className="svc-page">
    <PageNavbar />

    <section className="svc-hero">
      <div className="svc-hero-glow" />
      <div className="svc-container svc-hero-inner">
        <motion.div className="svc-hero-text" {...getRevealProps('fadeUp')}>
          <span className="svc-eyebrow">Certifications & Expertise</span>
          <h1 className="svc-hero-title">Credentials That Back Up the Work.</h1>
          <p className="svc-hero-desc">
            Certifications are not a marketing badge here — they are the reason our findings hold
            up under scrutiny, our compliance advice matches what the auditor will actually ask
            for, and our reports read like they were written by someone who has done this before.
          </p>
          <div className="svc-hero-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary">
              Get In Touch <FaArrowRight />
            </Link>
            <Link to="/leadership" className="svc-btn svc-btn-ghost">
              How Our Team Is Structured
            </Link>
          </div>
        </motion.div>

        <motion.aside className="svc-hero-card" {...getRevealProps('slideRight', 0.12)}>
          <span className="svc-card-tag">At a glance</span>
          <div className="svc-stats">
            <div className="svc-stat"><div className="svc-stat-value">7</div><div className="svc-stat-label">Certifications Held</div></div>
            <div className="svc-stat"><div className="svc-stat-value">20+</div><div className="svc-stat-label">Years of Leadership Experience</div></div>
            <div className="svc-stat"><div className="svc-stat-value">100%</div><div className="svc-stat-label">Audit Success Rate</div></div>
          </div>
        </motion.aside>
      </div>
    </section>

    <section className="svc-section">
      <div className="svc-container">
        <header className="svc-section-head">
          <span className="svc-section-tag">What We Hold</span>
          <h2 className="svc-section-title">Seven Credentials, Seven Different Jobs</h2>
          <p className="svc-section-sub">
            Each certification maps to a different part of the work — hands-on exploitation,
            governance, audit readiness, and methodology. None of them are interchangeable, and we
            deliberately cover all seven rather than concentrating on one.
          </p>
        </header>

        <motion.div
          className="svc-feature-grid"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={{ ...staggerContainer, visible: { transition: { staggerChildren: 0.08 } } }}
        >
          {certifications.map((c) => (
            <motion.article className="svc-feature-card" key={c.name} variants={staggerItem}>
              <div className="svc-feature-icon">{c.icon}</div>
              <h3 className="svc-feature-title">{c.name}</h3>
              <p className="svc-feature-desc" style={{ fontWeight: 700, color: '#d4d4dc', marginBottom: '0.5rem' }}>{c.full}</p>
              <p className="svc-feature-desc">{c.body}</p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>

    <section className="svc-cta">
      <div className="svc-container">
        <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
          <h2 className="svc-cta-title">Want to work with certified practitioners?</h2>
          <p className="svc-cta-desc">
            We respond to every inquiry within one business day.
          </p>
          <div className="svc-cta-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary">
              Book Consultation <FaArrowRight />
            </Link>
            <Link to="/services" className="svc-btn svc-btn-ghost-light">
              View All Services
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    <Footer />
  </div>
);

export default CertificationsPage;
